import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { StylesForm } from '../../../styles/global';
import {
  formatCourseLong,
  formatDepartmentLong,
} from '../../../utils/formatAttributes';
import { useAuth } from '../../../utils/AuthContext';
import { useEffect, useState } from 'react';
import {
  ClientWizard,
  ExpectedNumberOfMembersWizard,
  ProjectIdentityWizard,
} from './Wizards';
import { ArrowRightAlt } from '@mui/icons-material';
import { createAward, getRubrics, patchProject } from '../../../utils/API';
import { useFeedback } from '../../../utils/FeedbackContext';
import { AwardIcon, AwardIcons } from '../../AwardIcon';

const SlateBaseText = ({ children }) => {
  return (
    <>
      <Typography {...StylesForm.text('p')} color="darkslategray">
        {children}
      </Typography>
    </>
  );
};

export const AddAwardModal = ({ open, onClose, projectId }) => {
  const { notifyError, notifySuccess } = useFeedback();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rubric, setRubric] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [icon, setIcon] = useState('');
  const [availableRubrics, setAvailableRubrics] = useState([]);

  const handleSubmit = () => {
    if (!title || !description || !rubric || !year || !icon) {
      notifyError('Please fill in all required fields.');
      return;
    }
    createAward(projectId, {
      title,
      description,
      rubric,
      project: projectId,
      icon,
      year,
    })
      .then((res) => {
        notifySuccess('Award created successfully!');
        setTitle('');
        setDescription('');
        setRubric('');
        setYear(new Date().getFullYear());
        setIcon('');
        onClose();
        // reload after 3 seconds to show the new award in the list
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      })
      .catch((err) => {
        notifyError(
          err?.response?.data?.message ||
            'Failed to create award. Please try again.',
        );
      });
  };
  useEffect(() => {
    const fetchRubrics = async () => {
      try {
        const res = await getRubrics('');
        setAvailableRubrics(res?.data?.data || []);
      } catch (error) {
        notifyError('Failed to fetch rubrics.');
      }
    };
    fetchRubrics();
  }, [notifyError]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    bgcolor: 'background.paper',
    border: '2px solid #a0a0a0',
    boxShadow: 24,
    p: 4,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Typography {...StylesForm.text('h6')} marginBottom={2}>
          Add Award
        </Typography>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
          />
          <TextField
            label="Year"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            fullWidth
            required
          />
          <FormControl fullWidth required>
            <InputLabel id="demo-simple-select-label">Rubric</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={rubric}
              label="Rubric"
              onChange={(e) => setRubric(e.target.value)}
            >
              {availableRubrics?.map((rubric) => (
                <MenuItem key={rubric._id} value={rubric._id}>
                  <Box display="flex" gap={2} alignItems={'center'}>
                    {rubric?.title}
                    <Chip label={rubric?.type} />
                    {rubric?.course !== 'N/A' && (
                      <Chip label={rubric?.course} />
                    )}
                    {rubric?.department !== 'N/A' && (
                      <Chip label={rubric?.department} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth required>
            <InputLabel id="icon-select-label">Icon</InputLabel>
            <Select
              labelId="icon-select-label"
              id="icon-select"
              value={icon}
              label="Icon"
              onChange={(e) => setIcon(e.target.value)}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }}
            >
              {AwardIcons.map((icon) => (
                <MenuItem key={icon} value={icon}>
                  <Box display="flex" gap={1} alignItems={'center'}>
                    <AwardIcon icon={icon} /> {icon}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="contained" onClick={handleSubmit}>
            Create Award
          </Button>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Widget for Project Details
const InformProjectDetails = ({ project }) => {
  const { user } = useAuth();
  const IsAdmin = user?.role === 'ADMIN' || user?.role === 'FACULTY';
  const IsStudent = user?.role === 'STUDENT';
  const [cta, setCta] = useState('edit');
  const [form, setForm] = useState(undefined);
  const { notifyError, notifySuccess } = useFeedback();
  const [awardModalOpen, setAwardModalOpen] = useState(false);

  const handleSaveChanges = () => {
    patchProject(project.data._id, form)
      .then((res) => {
        notifySuccess('Project updated successfully!');
        setCta('edit');

        setTimeout(() => {
          window.location.reload();
        }, 4000);
      })
      .catch((err) => {
        notifyError(
          err?.response?.data?.message || 'Failed to update project.',
        );
      });
  };

  useEffect(() => {
    if (form === undefined && !!project) {
      setForm({
        ...project.data,
      });
    }
  }, [project, form]);

  const disabled =
    form?.title === project?.data.title &&
    form?.description === project?.data.description &&
    form?.clients === project?.data.clients &&
    form?.expectedNumberOfMembers === project?.data.expectedNumberOfMembers;

  const viewProject = !!project && (
    <>
      <Typography {...StylesForm.text('p')}>
        Project Title:{' '}
        <SlateBaseText>
          <strong>{`${project.data.title}`}</strong>
        </SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Course Equivalent:{' '}
        <SlateBaseText>{`${formatCourseLong(
          project.data.course,
        )}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Department:{' '}
        <SlateBaseText>{`${formatDepartmentLong(
          project.data.department,
        )}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Competition Participant Year:{' '}
        <SlateBaseText>{`${project.data.year}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Client(s): <SlateBaseText>{`${project.data.clients}`}</SlateBaseText>
      </Typography>

      <Typography {...StylesForm.text('p')}>
        Instructor(s):{' '}
        <SlateBaseText>{`${project.data.instructor}`}</SlateBaseText>
      </Typography>
    </>
  );

  const changesView = !!form && !!project && (
    <Box
      sx={{
        border: '1px dashed lightgray',
        padding: 2,
        borderRadius: 2,
        marginY: 1,
      }}
    >
      <SlateBaseText>Current Changes* </SlateBaseText>
      {form.title !== project.data.title && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Project Title:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.title}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.title}`} />
          </Box>
        </>
      )}
      {form.clients !== project.data.clients && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Project Clients:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.clients}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.clients}`} />
          </Box>
        </>
      )}
      {form.expectedNumberOfMembers !==
        project.data.expectedNumberOfMembers && (
        <>
          <Typography {...StylesForm.text('body2')} color="gray">
            Expected # of Members:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginY: 1,
              flexWrap: 'wrap',
              padding: 1,
              border: '1px solid lightgrey',
              borderRadius: 8,
              alignContent: 'center',
            }}
          >
            <Chip label={`From: ${project.data.expectedNumberOfMembers}`} />
            <ArrowRightAlt />
            <Chip label={`To: ${form.expectedNumberOfMembers}`} />
          </Box>
        </>
      )}
    </Box>
  );

  const editProject = !!project && (
    <>
      <Divider sx={{ marginY: 1 }} />
      <Box
        sx={{
          border: '1px dashed lightgray',
          padding: 2,
          borderRadius: 2,
          gap: 2,
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <ProjectIdentityWizard
          title={`Edit Project Identity`}
          variant={'edit'}
          buttonLabel={'Edit Project Title'}
          onChange={(title) => setForm({ ...form, title })}
        />
        <ClientWizard
          title={`Edit Client(s)`}
          variant={'edit'}
          buttonLabel={'Edit Client(s)'}
          onChange={(clients) => setForm({ ...form, clients })}
        />
        <ExpectedNumberOfMembersWizard
          title={`Project Team Members`}
          variant={'edit'}
          buttonLabel={'Project Team Members'}
          onChange={(expectedNumberOfMembers) =>
            setForm({ ...form, expectedNumberOfMembers })
          }
        />
      </Box>
      {changesView}
      {IsStudent && (
        <Alert variant="outlined" severity="info" fontFamily={'sans-serif'}>
          You can only edit the project title, clients, and expected number of
          members. For other changes, please contact an administrator. Don't
          forget to save your changes above!
        </Alert>
      )}
    </>
  );

  return (
    <>
      {project && (
        <>
          {/* Title: Project Details */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography {...StylesForm.text('h6')}>Project Details</Typography>
            <Box gap={1} display="flex">
              {(IsAdmin || IsStudent) && (
                <>
                  {cta === 'edit' && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setCta('save')}
                    >
                      Edit Project
                    </Button>
                  )}
                  {cta === 'save' && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSaveChanges}
                      disabled={disabled}
                    >
                      Save Changes
                    </Button>
                  )}
                </>
              )}
              {IsAdmin && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setAwardModalOpen(true)}
                >
                  Add 🏆
                </Button>
              )}
            </Box>
          </Box>

          <Divider />

          {/* Start of Info: Title, Course, Department, Year, Clients, Instructor */}

          {cta === 'edit' && viewProject}
          {cta === 'save' && editProject}

          {IsAdmin && (
            <AddAwardModal
              open={awardModalOpen}
              onClose={() => setAwardModalOpen(false)}
              projectId={project.data._id}
            />
          )}

          {/* End of Info */}

          <Divider />
        </>
      )}
    </>
  );
};

export default InformProjectDetails;
